import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from "@mui/material";

// Last picked fields, so the admin does not re-tick the same boxes on every
// export. Per-browser convenience only; storage may be unavailable.
const STORAGE_KEY = "admin.subscriptionsExport.fields";

function loadSavedFields() {
  try {
    const v = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    return Array.isArray(v) ? v : null;
  } catch (_) {
    return null;
  }
}

function saveFields(keys) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch (_) {}
}

// Pick the fields, then download every subscription of the workspaces in
// view as an .xlsx grouped by legal name. The field list comes from the
// backend so the picker and the sheet never drift apart.
const ExportSubscriptionsDialog = ({ open, onClose, workIds }) => {
  const [columns, setColumns] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || columns.length) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(
      `${process.env.REACT_APP_SERVER_URL}/admin/subscriptions_export/columns`,
      { credentials: "include", headers: { Accept: "application/json" } },
    )
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        const cols = Array.isArray(json?.columns) ? json.columns : [];
        if (!cols.length) {
          setError(json?.msg || "Could not load the field list.");
          return;
        }
        const known = new Set(cols.map((c) => c.key));
        const saved = (loadSavedFields() || []).filter((k) => known.has(k));
        const locked = cols.filter((c) => c.locked).map((c) => c.key);
        setColumns(cols);
        setSelected(
          new Set(saved.length ? [...locked, ...saved] : cols.map((c) => c.key)),
        );
      })
      .catch(() => {
        if (!cancelled) setError("Could not load the field list.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, columns.length]);

  const sections = useMemo(() => {
    const out = [];
    for (const c of columns) {
      let s = out.find((x) => x.name === c.section);
      if (!s) {
        s = { name: c.section, columns: [] };
        out.push(s);
      }
      s.columns.push(c);
    }
    return out;
  }, [columns]);

  const toggle = (key) => {
    if (lockedKeys.has(key)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Locked fields (Legal name — the sheet is grouped on it) never untick.
  const lockedKeys = useMemo(
    () => new Set(columns.filter((c) => c.locked).map((c) => c.key)),
    [columns],
  );

  const setMany = (keys, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (on) next.add(k);
        else if (!lockedKeys.has(k)) next.delete(k);
      }
      return next;
    });
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError("");
    // Sheet column order follows the field list, not click order.
    const keys = columns.map((c) => c.key).filter((k) => selected.has(k));
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/subscriptions_export`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ work_ids: workIds, columns: keys }),
        },
      );
      const type = res.headers.get("content-type") || "";
      if (!res.ok || !type.includes("spreadsheetml")) {
        const json = await res.json().catch(() => ({}));
        setError(json?.msg || "Export failed.");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `subscriptions-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      saveFields(keys);
      onClose();
    } catch (e) {
      setError(e?.message || "Export failed.");
    } finally {
      setDownloading(false);
    }
  };

  const allKeys = columns.map((c) => c.key);

  return (
    <Dialog
      open={open}
      onClose={downloading ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ pb: 0.5 }}>Export subscriptions</DialogTitle>
      <DialogContent dividers>
        <p style={{ margin: "0 0 12px", color: "#607084", fontSize: 14 }}>
          {workIds.length} workspace{workIds.length === 1 ? "" : "s"} in this
          view. Each customer's legal name sits in one cell beside all of
          their subscriptions.
        </p>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        )}
        {!loading && columns.length > 0 && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <Button size="small" onClick={() => setMany(allKeys, true)}>
                Select all
              </Button>
              <Button size="small" onClick={() => setMany(allKeys, false)}>
                Clear
              </Button>
            </div>
            {sections.map((section) => {
              const keys = section.columns
                .map((c) => c.key)
                .filter((k) => !lockedKeys.has(k));
              const onCount = keys.filter((k) => selected.has(k)).length;
              return (
                <div key={section.name} style={{ marginBottom: 12 }}>
                  <FormControlLabel
                    label={
                      <strong style={{ fontSize: 14 }}>{section.name}</strong>
                    }
                    control={
                      <Checkbox
                        size="small"
                        checked={onCount === keys.length}
                        indeterminate={onCount > 0 && onCount < keys.length}
                        onChange={() => setMany(keys, onCount !== keys.length)}
                      />
                    }
                  />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                      paddingLeft: 28,
                    }}
                  >
                    {section.columns.map((c) => (
                      <FormControlLabel
                        key={c.key}
                        label={
                          <span style={{ fontSize: 13.5 }}>
                            {c.label}
                            {c.locked && (
                              <span style={{ color: "#8696aa" }}> · always</span>
                            )}
                          </span>
                        }
                        disabled={!!c.locked}
                        control={
                          <Checkbox
                            size="small"
                            checked={c.locked || selected.has(c.key)}
                            onChange={() => toggle(c.key)}
                          />
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}
        {error && (
          <div style={{ color: "#c0392b", fontSize: 13, marginTop: 8 }}>
            {error}
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <span style={{ marginRight: "auto", color: "#607084", fontSize: 13 }}>
          {selected.size} of {columns.length} fields
        </span>
        <Button onClick={onClose} disabled={downloading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={downloading || loading || selected.size === 0}
        >
          {downloading ? "Preparing…" : "Download .xlsx"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportSubscriptionsDialog;
